/**
 * <ry-tree>
 *
 * Tree view with CSS-only expand/collapse using hidden checkboxes
 * and grid-template-rows animation. Supports drag-and-drop rearranging
 * and JSON serialization.
 *
 * Usage:
 * <ry-tree>
 *   <ry-tree-item label="src" open>
 *     <ry-tree-item label="app" open>
 *       <ry-tree-item label="layout.tsx"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree-item>
 * </ry-tree>
 *
 * Drag and drop:
 * <ry-tree draggable>...</ry-tree>
 *
 * JS uses data-ry-target for queries, CSS uses .ry-tree__* for styling.
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

let treeIdCounter = 0;

const DRAG_THRESHOLD = 5;

function icon16(name: string): string {
  return getIcon(name)
    .replace(/width="24"/g, 'width="16"')
    .replace(/height="24"/g, 'height="16"')
    .replace(/viewBox/, 'class="ry-tree__icon" viewBox');
}

export interface TreeNode {
  label: string;
  children?: TreeNode[];
  open?: boolean;
  selected?: boolean;
}

export class RyTree extends RyElement {
  // Drag state
  #dragPending = false;
  #dragActive = false;
  #dragOriginX = 0;
  #dragOriginY = 0;
  #draggedItem: HTMLElement | null = null;
  #ghost: HTMLElement | null = null;
  #dropTarget: HTMLElement | null = null;
  #dropPosition: 'before' | 'after' | 'inside' | null = null;

  setup(): void {
    this.#build();

    // Delegate click for file selection
    this.on(this, 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      const fileItem = target.closest<HTMLElement>('[data-ry-target="file"]');
      if (!fileItem) return;

      this.$$('[data-ry-selected]').forEach(el => el.removeAttribute('data-ry-selected'));
      fileItem.setAttribute('data-ry-selected', '');

      this.emit('select', {
        label: fileItem.dataset.ryLabel ?? '',
      });
    });

    // Emit toggle events when folders open/close
    this.on(this, 'change', (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (!input.classList.contains('ry-tree__toggle')) return;

      const li = input.closest<HTMLElement>('.ry-tree__item');
      const label = li?.querySelector<HTMLElement>('[data-ry-target="folder"]')?.dataset.ryLabel ?? '';

      this.emit('toggle', {
        label,
        open: input.checked,
      });
    });

    // Drag and drop — only when draggable attribute is present
    if (this.hasAttribute('sortable')) {
      this.#bindDrag();
    }
  }

  // ── Build ──────────────────────────────────────────────────

  #build(): void {
    if (this.$('.ry-tree__root')) return;

    const items = Array.from(this.children) as HTMLElement[];
    const ul = document.createElement('ul');
    ul.className = 'ry-tree__root';

    for (const item of items) {
      ul.appendChild(this.#buildItem(item));
    }

    this.innerHTML = '';
    this.appendChild(ul);
  }

  #buildItem(source: HTMLElement): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'ry-tree__item';

    const label = source.getAttribute('label') ?? '';
    const isOpen = source.hasAttribute('open');
    const isSelected = source.hasAttribute('selected');
    const children = Array.from(source.children).filter(
      c => c.tagName === 'RY-TREE-ITEM'
    ) as HTMLElement[];
    const isFolder = children.length > 0;

    if (isFolder) {
      const id = `ry-tree-${++treeIdCounter}`;

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = id;
      checkbox.className = 'ry-tree__toggle';
      if (isOpen) checkbox.checked = true;

      const labelEl = document.createElement('label');
      labelEl.htmlFor = id;
      labelEl.className = 'ry-tree__label';
      labelEl.setAttribute('data-ry-target', 'folder');
      labelEl.dataset.ryLabel = label;
      labelEl.innerHTML = `
        <span class="ry-tree__icon-closed">${icon16('folder')}</span>
        <span class="ry-tree__icon-open">${icon16('folder-open')}</span>
        ${label}
      `;

      const wrapper = document.createElement('div');
      wrapper.className = 'ry-tree__children-wrapper';

      const childUl = document.createElement('ul');
      childUl.className = 'ry-tree__children';

      for (const child of children) {
        childUl.appendChild(this.#buildItem(child));
      }

      wrapper.appendChild(childUl);
      li.appendChild(checkbox);
      li.appendChild(labelEl);
      li.appendChild(wrapper);
    } else {
      const fileEl = document.createElement('div');
      fileEl.className = 'ry-tree__file';
      fileEl.setAttribute('data-ry-target', 'file');
      fileEl.dataset.ryLabel = label;
      if (isSelected) fileEl.setAttribute('data-ry-selected', '');
      fileEl.innerHTML = `${icon16('file')} ${label}`;

      li.appendChild(fileEl);
    }

    return li;
  }

  // ── Serialization ──────────────────────────────────────────

  /** Serialize the current tree state to JSON. */
  toJSON(): TreeNode[] {
    const root = this.$<HTMLElement>('.ry-tree__root');
    if (!root) return [];
    return this.#serializeChildren(root);
  }

  /** Alias for toJSON(). */
  get value(): TreeNode[] {
    return this.toJSON();
  }

  #serializeChildren(container: HTMLElement): TreeNode[] {
    const items = Array.from(container.children).filter(
      (c): c is HTMLElement => c instanceof HTMLElement && c.classList.contains('ry-tree__item')
    );
    return items.map(item => this.#serializeItem(item));
  }

  #serializeItem(item: HTMLElement): TreeNode {
    const folderLabel = item.querySelector<HTMLElement>(':scope > [data-ry-target="folder"]');
    const fileDiv = item.querySelector<HTMLElement>(':scope > [data-ry-target="file"]');

    if (folderLabel) {
      const toggle = item.querySelector<HTMLInputElement>(':scope > .ry-tree__toggle');
      const childrenList = item.querySelector<HTMLElement>('.ry-tree__children');
      const node: TreeNode = { label: folderLabel.dataset.ryLabel ?? '' };
      if (toggle?.checked) node.open = true;
      if (childrenList) {
        const children = this.#serializeChildren(childrenList);
        if (children.length > 0) node.children = children;
      }
      return node;
    }

    // File
    const node: TreeNode = { label: fileDiv?.dataset.ryLabel ?? '' };
    if (fileDiv?.hasAttribute('data-ry-selected')) node.selected = true;
    return node;
  }

  /** Create a tree element from JSON data. */
  static from(data: TreeNode[]): RyTree {
    const tree = document.createElement('ry-tree') as RyTree;
    for (const node of data) {
      tree.appendChild(RyTree.#nodeToElement(node));
    }
    return tree;
  }

  static #nodeToElement(node: TreeNode): HTMLElement {
    const item = document.createElement('ry-tree-item');
    item.setAttribute('label', node.label);
    if (node.open) item.setAttribute('open', '');
    if (node.selected) item.setAttribute('selected', '');
    if (node.children) {
      for (const child of node.children) {
        item.appendChild(RyTree.#nodeToElement(child));
      }
    }
    return item;
  }

  // ── Drag and Drop ──────────────────────────────────────────

  #bindDrag(): void {
    this.on(this, 'pointerdown', this.#onPointerDown);
    this.on(document, 'pointermove', this.#onPointerMove);
    this.on(document, 'pointerup', this.#onPointerUp);
  }

  #onPointerDown = (e: PointerEvent): void => {
    const target = e.target as HTMLElement;
    const handle = target.closest<HTMLElement>('.ry-tree__label, .ry-tree__file');
    if (!handle) return;

    const item = handle.closest<HTMLElement>('.ry-tree__item');
    if (!item) return;

    // Record origin — don't start drag yet (distance threshold)
    this.#dragPending = true;
    this.#dragOriginX = e.clientX;
    this.#dragOriginY = e.clientY;
    this.#draggedItem = item;
  };

  #onPointerMove = (e: PointerEvent): void => {
    if (!this.#dragPending && !this.#dragActive) return;

    // Check distance threshold before activating
    if (this.#dragPending && !this.#dragActive) {
      const dx = e.clientX - this.#dragOriginX;
      const dy = e.clientY - this.#dragOriginY;
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

      // Threshold crossed — activate drag
      this.#dragPending = false;
      this.#dragActive = true;
      this.#activateDrag(e);
    }

    if (!this.#dragActive || !this.#ghost) return;

    // Update ghost position
    this.#ghost.style.left = `${e.clientX + 12}px`;
    this.#ghost.style.top = `${e.clientY - 14}px`;

    // Hide ghost momentarily so elementFromPoint sees through it
    this.#ghost.style.display = 'none';
    const elUnder = document.elementFromPoint(e.clientX, e.clientY);
    this.#ghost.style.display = '';

    if (!elUnder) {
      this.#clearIndicator();
      return;
    }

    const targetHandle = elUnder.closest<HTMLElement>('.ry-tree__label, .ry-tree__file');
    if (!targetHandle) {
      this.#clearIndicator();
      return;
    }

    const targetItem = targetHandle.closest<HTMLElement>('.ry-tree__item');
    if (!targetItem || targetItem === this.#draggedItem) {
      this.#clearIndicator();
      return;
    }

    // Don't allow dropping into own descendants
    if (this.#draggedItem?.contains(targetItem)) {
      this.#clearIndicator();
      return;
    }

    // Determine position: top third / middle third / bottom third
    const rect = targetHandle.getBoundingClientRect();
    const relY = e.clientY - rect.top;
    const h = rect.height;
    const isFolder = targetItem.querySelector(':scope > .ry-tree__toggle') !== null;

    let position: 'before' | 'after' | 'inside';
    if (isFolder && relY > h * 0.25 && relY < h * 0.75) {
      position = 'inside';
    } else if (relY < h * 0.5) {
      position = 'before';
    } else {
      position = 'after';
    }

    this.#setIndicator(targetItem, position);
  };

  #onPointerUp = (): void => {
    if (this.#dragActive) {
      this.#finishDrag();
    }
    this.#dragPending = false;
    this.#dragActive = false;
    this.#draggedItem = null;
  };

  #activateDrag(e: PointerEvent): void {
    if (!this.#draggedItem) return;

    // Prevent the click from toggling the folder checkbox
    e.preventDefault();

    // Mark as dragging and collapse to thin placeholder so parent targets are reachable
    this.#draggedItem.setAttribute('data-ry-dragging', '');
    this.#draggedItem.classList.add('ry-tree__item--collapsed');

    // Create ghost
    const handle = this.#draggedItem.querySelector<HTMLElement>('.ry-tree__label, .ry-tree__file');
    if (!handle) return;

    const ghost = handle.cloneNode(true) as HTMLElement;
    ghost.className = 'ry-tree__ghost';
    // Copy label/file styles for size
    ghost.style.display = 'flex';
    ghost.style.alignItems = 'center';
    ghost.style.gap = 'var(--ry-space-2, 0.5rem)';
    ghost.style.padding = 'var(--ry-space-1, 0.25rem) var(--ry-space-2, 0.5rem)';
    ghost.style.height = '28px';
    ghost.style.fontSize = 'var(--ry-text-sm, 0.875rem)';
    ghost.style.whiteSpace = 'nowrap';
    ghost.style.left = `${e.clientX + 12}px`;
    ghost.style.top = `${e.clientY - 14}px`;
    document.body.appendChild(ghost);
    this.#ghost = ghost;
  }

  #finishDrag(): void {
    // Remove ghost
    if (this.#ghost?.parentNode) {
      this.#ghost.parentNode.removeChild(this.#ghost);
    }
    this.#ghost = null;

    // Clear visual
    this.#draggedItem?.classList.remove('ry-tree__item--collapsed');
    this.#draggedItem?.removeAttribute('data-ry-dragging');
    const dropTarget = this.#dropTarget;
    const dropPosition = this.#dropPosition;
    this.#clearIndicator();

    // Perform DOM move
    if (this.#draggedItem && dropTarget && dropPosition) {
      this.#performMove(this.#draggedItem, dropTarget, dropPosition);
    }
  }

  #setIndicator(item: HTMLElement, position: 'before' | 'after' | 'inside'): void {
    if (this.#dropTarget === item && this.#dropPosition === position) return;
    this.#clearIndicator();
    item.setAttribute('data-ry-drop-target', position);
    this.#dropTarget = item;
    this.#dropPosition = position;
  }

  #clearIndicator(): void {
    this.#dropTarget?.removeAttribute('data-ry-drop-target');
    this.#dropTarget = null;
    this.#dropPosition = null;
  }

  #performMove(
    draggedItem: HTMLElement,
    dropTarget: HTMLElement,
    position: 'before' | 'after' | 'inside'
  ): void {
    // Safety: can't drop inside own descendant (circular)
    if (position === 'inside' && dropTarget.contains(draggedItem)) return;

    const itemLabel = this.#itemLabel(draggedItem);
    const targetLabel = this.#itemLabel(dropTarget);

    if (position === 'before') {
      dropTarget.parentNode?.insertBefore(draggedItem, dropTarget);
    } else if (position === 'after') {
      dropTarget.parentNode?.insertBefore(draggedItem, dropTarget.nextSibling);
    } else {
      // Inside — append to folder's children list
      const childrenList = dropTarget.querySelector<HTMLElement>('.ry-tree__children');
      if (childrenList) {
        childrenList.appendChild(draggedItem);
        // Auto-open the folder
        const toggle = dropTarget.querySelector<HTMLInputElement>(':scope > .ry-tree__toggle');
        if (toggle && !toggle.checked) {
          toggle.checked = true;
        }
      }
    }

    this.emit('move', {
      item: itemLabel,
      target: targetLabel,
      position,
    });
  }

  #itemLabel(item: HTMLElement): string {
    const folder = item.querySelector<HTMLElement>(':scope > [data-ry-target="folder"]');
    const file = item.querySelector<HTMLElement>(':scope > [data-ry-target="file"]');
    return folder?.dataset.ryLabel ?? file?.dataset.ryLabel ?? '';
  }
}

export class RyTreeItem extends RyElement {
  // Declarative container consumed by RyTree#build
}

customElements.define('ry-tree', RyTree);
customElements.define('ry-tree-item', RyTreeItem);
